import * as React from "react";
import { FunctionComponent, ReactElement, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { convertToDisplayValue, ModelSpec } from "./AckModelForm";

enum SortType {ASC = "ASC", DESC = "DESC"}

const sortComparatorFn = (sortColumnIndex: number, type: SortType) => {
  return (a: any, b: any) => {
    const spec = a.spec[sortColumnIndex];
    if (a.payload[spec.name] < b.payload[spec.name]) return type == SortType.ASC ? -1 : 1;
    if (a.payload[spec.name] > b.payload[spec.name]) return type == SortType.ASC ? 1 : -1;
    return 0;
  };
};

const defaultRowFormatters = (spec: ModelSpec, payload: any, _: number): ReactElement | string => {
  return convertToDisplayValue(spec, payload) || "";
};

export type AckTableRowProps = {
  spec: ModelSpec[];
  payload: any;
}

export type AckTableRowFormatter = (spec: ModelSpec, payload: any, rowIndex: number) => any;

export type AckTableProps = {
  rows: AckTableRowProps[];
  footer?: { [key: string]: any };
  actions?: FunctionComponent<AckTableRowProps>;
  formatters?: AckTableRowFormatter;
  hideIndexCol?: boolean;
  enableSortByHeader?: boolean;
}

export const AckTable: FunctionComponent<AckTableProps> = (props) => {
  const [sortColumnIndex, setSortColumnIndex] = useState(0);
  const [sortOrderType, setSortOrderType] = useState(SortType.DESC);
  const shouldShowIndexCol = !props.hideIndexCol;
  const shouldAllowSorting = props.enableSortByHeader || true;
  const sortedRows = props.rows.sort(sortComparatorFn(sortColumnIndex, sortOrderType));
  const spec = props.rows[0].spec;

  const toggleSort = (valueIndex: number) => {
    setSortOrderType(sortOrderType == SortType.DESC ? SortType.ASC : SortType.DESC);
    setSortColumnIndex(valueIndex);
  };
  const renderSortedHeaderRow = (spec: ModelSpec, valueIndex: number) => {
    let icon = (<FontAwesomeIcon icon="sort" size="xs" fixedWidth={true}/>);
    if (sortColumnIndex == valueIndex) {
      icon = sortOrderType == SortType.ASC
        ? (<FontAwesomeIcon icon="sort-up" size="xs" fixedWidth={true}/>)
        : (<FontAwesomeIcon icon="sort-down" size="xs" fixedWidth={true}/>);
    }
    return (<th className="is-clickable"
                key={`key-${valueIndex}`}
                onClick={() => toggleSort(valueIndex)}>{spec.label} {icon}</th>);
  };
  const renderNonSortedHeaderRow = (spec: ModelSpec, valueIndex: number) => {
    return (<th key={`key-${valueIndex}`}>{spec.label}</th>);
  };
  const renderHeaderRow = () => {
    return (
      <tr>
        {shouldShowIndexCol && <th>#</th>}
        {spec.map((value, valueIndex) => shouldAllowSorting
          ? renderSortedHeaderRow(value, valueIndex)
          : renderNonSortedHeaderRow(value, valueIndex))}
        {props.actions && <th>Actions</th>}
      </tr>
    );
  };
  const renderBodyRows = () => sortedRows.map((row, rowIndex) => {
    const rowFormatters = props.formatters || defaultRowFormatters;
    const rowValues = row.spec.map(spec => rowFormatters(spec, row.payload, rowIndex));
    return (
      <tr key={`key-body-${rowIndex + 1}`}>
        {shouldShowIndexCol && <th key={`key-${rowIndex + 1}`}>{rowIndex + 1}</th>}
        {rowValues.map((value, valueIndex) => (<td key={`key-${valueIndex}`}>{value}</td>))}
        {props.actions && props.actions(row)}
      </tr>
    );
  });
  const footer = props.footer || {};
  const renderFooterRows = () => Object.entries(footer).map((entry, index) => {
    return (
      <tr key={`key-footer-${index + 1}`}>
        <td colSpan={spec.length}><strong>{entry[0]}</strong></td>
        <td className="is-align-items-end"><strong>{entry[1]}</strong></td>
      </tr>
    );
  });
  return (
    <div className="table-container">
      <table className="table is-hoverable is-fullwidth">
        <thead>{renderHeaderRow()}</thead>
        <tbody>{renderBodyRows()}</tbody>
        {props.footer && (<tfoot>{renderFooterRows()}</tfoot>)}
      </table>
    </div>
  );
};
